import { create } from 'zustand';
import { ListFamilyMembers, CreateFamilyMember, GetFamilyMember, UpdateFamilyMember, DeleteFamilyMember } from '../api/api';
import toast from 'react-hot-toast';

export interface FamilyMember {
  _id: string;
  name: string;
  dob?: string;
  education?: string;
  job?: string;
  baptismDate?: string;
  firstCommunionDate?: string;
  confirmationDate?: string;
  married: boolean;
  marriedDate?: string;
  deathDate?: string;
  mother?: string | { _id: string; name: string };
  father?: string | { _id: string; name: string };
  contactNumbers: string[];
  family: string | { _id: string; familyName: string; guardian: string; unit?: string | { _id: string; name: string; unitNumber: string } };
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateFamilyMemberI {
  name: string;
  dob?: string;
  education?: string;
  job?: string;
  baptismDate?: string;
  firstCommunionDate?: string;
  confirmationDate?: string;
  married?: boolean;
  marriedDate?: string;
  deathDate?: string;
  mother?: string;
  father?: string;
  contactNumbers?: string[];
  family: string;
}

interface UpdateFamilyMemberI {
  name?: string;
  dob?: string;
  education?: string;
  job?: string;
  baptismDate?: string;
  firstCommunionDate?: string;
  confirmationDate?: string;
  married?: boolean;
  marriedDate?: string;
  deathDate?: string;
  mother?: string;
  father?: string;
  contactNumbers?: string[];
  family?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetFamilyMembersResponse {
  data: {
    members: FamilyMember[];
    pagination?: PaginationInfo;
  };
}

interface FamilyMemberResponse {
  data: {
    member: FamilyMember;
  };
  status: boolean;
  message: string;
}

interface DeleteFamilyMemberResponse {
  status: boolean;
  message: string;
}

interface FamilyMemberStore {
  members: FamilyMember[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getFamilyMembers: (familyId?: string, search?: string, page?: number, limit?: number) => Promise<void>;
  createFamilyMember: (member: CreateFamilyMemberI) => Promise<void>;
  getFamilyMember: (id: string) => Promise<FamilyMember | null>;
  updateFamilyMember: (id: string, member: UpdateFamilyMemberI) => Promise<void>;
  deleteFamilyMember: (id: string) => Promise<void>;
}

export const useFamilyMemberStore = create<FamilyMemberStore>((set, get) => ({
  members: [] as FamilyMember[],
  loading: false,
  error: null,
  pagination: null,

  getFamilyMembers: async (familyId?: string, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListFamilyMembers<GetFamilyMembersResponse>(familyId, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      members: data.data.members || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createFamilyMember: async (member: CreateFamilyMemberI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateFamilyMember<FamilyMemberResponse>(member);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create family member' });
      return;
    }

    const members = get().members;
    set({ members: [data.data.member, ...members], loading: false });
    toast.success('Family member created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getFamilyMembers(member.family, undefined, currentPagination?.page, currentPagination?.limit);
  },

  getFamilyMember: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetFamilyMember<FamilyMemberResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get family member' });
      return null;
    }

    set({ loading: false });
    return data.data.member;
  },

  updateFamilyMember: async (id: string, member: UpdateFamilyMemberI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateFamilyMember<FamilyMemberResponse>(id, member);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update family member' });
      return;
    }

    const members = get().members;
    const updatedMembers = members.map((m) => (m._id === id ? { ...m, ...data.data.member } : m));
    set({ members: updatedMembers, loading: false });
    toast.success('Family member updated successfully');
    // Don't auto-refresh here - let the page component handle it with current filter state
  },

  deleteFamilyMember: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteFamilyMember<DeleteFamilyMemberResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete family member' });
      return;
    }

    const members = get().members.filter((member) => member._id !== id);
    set({ members, loading: false });
    toast.success('Family member deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    const deletedMember = get().members.find((m) => m._id === id);
    if (deletedMember) {
      const familyId = typeof deletedMember.family === 'string' ? deletedMember.family : deletedMember.family._id;
      get().getFamilyMembers(familyId, undefined, currentPagination?.page, currentPagination?.limit);
    }
  },
}));

export type { CreateFamilyMemberI, UpdateFamilyMemberI };
